import { useState, useRef, useEffect } from 'react';
import { FiBell, FiFilter, FiX } from 'react-icons/fi';

const ERROR_TYPES = ['All', 'IROP', 'API_ERROR', 'SSR'];
const PRIORITIES = ['All', 'High', 'Medium', 'Low'];

export const AlertsDropdown = ({ showAlerts, setShowAlerts, alerts = [], setAlerts }) => {
  const [selectedErrorType, setSelectedErrorType] = useState('All');
  const [selectedPriority, setSelectedPriority] = useState('All');
  const [showErrorTypeDropdown, setShowErrorTypeDropdown] = useState(false);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [expandedAlert, setExpandedAlert] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowAlerts(false);
      }
    };

    if (showAlerts) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAlerts, setShowAlerts]);

  const filteredAlerts = alerts.filter(alert => {
    const matchesErrorType = selectedErrorType === 'All' || alert.type?.toUpperCase() === selectedErrorType.toUpperCase();
    const matchesPriority = selectedPriority === 'All' || alert.priority?.toUpperCase() === selectedPriority.toUpperCase();
    return matchesErrorType && matchesPriority;
  });

  const getAlertColor = (color) => {
    const colors = {
      red: '#EF4444',
      yellow: '#F59E0B',
      blue: '#3B82F6'
    };
    return colors[color] || colors.blue;
  };

  const handleAlertClick = (alertId) => {
    setExpandedAlert(expandedAlert === alertId ? null : alertId);
  };

  if (!showAlerts) return null;

  return (
    <div className="alerts-dropdown" ref={dropdownRef}>
      <div className="alerts-header">
        <h3>Alerts</h3>
        <div className="alerts-header-actions">
          <button className="filter-btn" title="Filter" onClick={() => setShowFilters(_=>!_)}>
            <FiFilter size={16} />
          </button>
          <button className="close-btn" onClick={() => setShowAlerts(false)}>
            <FiX size={18} />
          </button>
        </div>
      </div>

   {showFilters ? <div className="alerts-filters">
        <div className="filter-dropdown">
          <button
            className="filter-button"
            onClick={() => {
              setShowErrorTypeDropdown(!showErrorTypeDropdown);
              setShowPriorityDropdown(false);
            }}
          >
            <span className="filter-icon">⊙</span>
            <span className="filter-label">Error Type</span>
            <span className="filter-value">{selectedErrorType}</span>
            <span className="chevron">▼</span>
          </button>

          {showErrorTypeDropdown && (
            <div className="filter-options">
              {ERROR_TYPES.map(type => (
                <button
                  type="button"
                  key={type}
                  className={`filter-option ${selectedErrorType === type ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedErrorType(type);
                    setShowErrorTypeDropdown(false);
                  }}
                >
                  {type}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="filter-dropdown">
          <button
            className="filter-button"
            onClick={() => {
              setShowPriorityDropdown(!showPriorityDropdown);
              setShowErrorTypeDropdown(false);
            }}
          >
            <span className="filter-icon">≡</span>
            <span className="filter-label">Priority</span>
            <span className="filter-value">{selectedPriority}</span>
            <span className="chevron">▼</span>
          </button>

          {showPriorityDropdown && (
            <div className="filter-options">
              {PRIORITIES.map(priority => (
                <button
                  type="button"
                  key={priority}
                  className={`filter-option ${selectedPriority === priority ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedPriority(priority);
                    setShowPriorityDropdown(false);
                  }}
                >
                  {priority}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>: null}

      <div className="alerts-list">
        {filteredAlerts.length === 0 ? (
          <div className="no-alerts">
            <FiBell size={48} />
            <p>No alerts found</p>
          </div>
        ) : (
          filteredAlerts.map(alert => (
            <div
              key={alert.id}
              className={`alert-item ${expandedAlert === alert.id ? 'expanded' : ''}`}
              style={{ borderLeftColor: getAlertColor(alert.color) }}
            >
              <div
                className="alert-content"
                role="button"
                tabIndex={0}
                aria-expanded={expandedAlert === alert.id}
                onClick={() => handleAlertClick(alert.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleAlertClick(alert.id);
                  }
                }}
              >
                <div className="alert-main">
                  <h4 className="alert-title">{alert.title}</h4>
                  {expandedAlert === alert.id && (
                    <button className="alert-close" onClick={(e) => {
                      e.stopPropagation();
                      setExpandedAlert(null);
                    }}>
                      <FiX size={14} />
                    </button>
                  )}
                </div>
                <p className="alert-description">{alert.description}</p>
                <div className="alert-meta">
                  <span className={`alert-badge priority-${alert.priority.toLowerCase()}`}>
                    {alert.priority}
                  </span>
                  <span className="alert-badge type-badge">
                    {alert.type}
                  </span>
                  {alert.flightNumber && (
                    <span className="alert-badge flight-badge">
                      {alert.flightNumber}
                    </span>
                  )}
                  <span className="alert-time">{alert.timestamp}</span>
                </div>

                {expandedAlert === alert.id && (
                  <div className="alert-details" style={{ display: 'flex', gap: '8px' }}>
                    <button className="view-details-btn">
                      View Details →
                    </button>
                    <button 
                      className="view-details-btn" 
                      style={{ 
                        borderColor: 'rgba(239, 68, 68, 0.3)', 
                        color: '#EF4444', 
                        background: 'transparent' 
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (setAlerts) {
                          setAlerts(prev => prev.filter(a => a.id !== alert.id));
                        }
                      }}
                    >
                      Dismiss
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AlertsDropdown;
